import { CircularProgress, styled, useTheme } from '@mui/material';
import { UserTrainingStatistics } from 'models/UserTrainingStatistics';
import MUIDataTable, { MUIDataTableColumn, MUIDataTableColumnDef, MUISortOptions } from 'mui-datatables';
import { StyledTableContainer, StyledLoadingContainer } from 'components/StatisticsDataTable/StatisticsDataTable.styles';
import { useTranslation } from 'react-i18next';
import { convertToStatisticDataTableDto } from 'components/StatisticsDataTable/Utils';
import { useRewardTypeDictionaries } from 'components/StatisticsDataTable/hooks/useRewardTypeDictionaries';
import { DataTableStatisticDto } from 'components/StatisticsDataTable/Dto/DataTableStatisticDto';
import { OverridenGlobalStyles } from 'components/StatisticsDataTable/OverridenGlobalStyles';
import { buildStatisticsCsv, generateFileName, triggerCsvDownload, triggerBlobDownload } from 'components/StatisticsDataTable/csvUtils';
import { getStatisticsColumns, isUncompleted } from 'components/StatisticsDataTable/Columns/StatisticTableColumns';
import { STATISTICS_TABLE_MIN_WIDTH } from 'components/StatisticsDataTable/Columns/columnWidths';
import { CustomStatisticsToolbar } from './CustomStatisticsToolbar';
import { ArchiveReportDialog } from 'components/StatisticsDataTable/ArchiveReportDialog';
import { useStatisticsWithArchives } from 'hooks/useStatisticsWithArchives';
import { useGenerateCsvCertificate } from 'hooks/useGenerateCsvCertificate';
import { useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { handleApiError } from 'helpers/handleApiError';

interface UserTrainingStatisticsTableProps {
  data: UserTrainingStatistics[];
  gettingStatistics: boolean;
}

type PendingDownload = {
  columns: MUIDataTableColumnDef[];
  rows: Array<{ data: unknown[] }>;
};

const CSV_META = { completedColumn: 'dateCompleted', pointsColumn: 'score' } as const;

const TIME_SPENT_COLUMN = 'timeSpent';

const StyledMUIDataTable = styled(MUIDataTable)`
  & .MuiTableCell-head span button {
    font-size: 16px !important;
  }
`;

export const UserTrainingStatisticsTable = (props: UserTrainingStatisticsTableProps) => {
  const { data, gettingStatistics } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { rewardTypeDictionary, revertedRewardDictionary } = useRewardTypeDictionaries();
  const { downloadStatisticsWithArchives, gettingStatisticsWithArchives } = useStatisticsWithArchives();
  const { generateCsvCertificate, generatingCsvCertificate } = useGenerateCsvCertificate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingDownload, setPendingDownload] = useState<PendingDownload | null>(null);
  const [sortOrder, setSortOrder] = useState<MUISortOptions | undefined>(undefined);

  const adjustedData: DataTableStatisticDto[] = useMemo(
    () => convertToStatisticDataTableDto(data, rewardTypeDictionary),
    [data, rewardTypeDictionary]
  );

  const columns = useMemo(
    () => getStatisticsColumns({ t, theme, revertedRewardDictionary }),
    [t, theme, revertedRewardDictionary]
  );

  const hidingUncompleted = sortOrder?.name === TIME_SPENT_COLUMN;

  const tableData = useMemo(
    () => (hidingUncompleted ? adjustedData.filter((row) => !isUncompleted(row.lessonProgressStatus)) : adjustedData),
    [adjustedData, hidingUncompleted]
  );

  const handleGenerateCertificate = async (lessonId: string, userIds: string[]) => {
    try {
      const { blob, fileName } = await generateCsvCertificate({ lessonId, userIds });
      triggerBlobDownload(blob, fileName);
    } catch (error) {
      handleApiError(error, { t, enqueueSnackbar });
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setPendingDownload(null);
  };

  const handleDownloadWithoutArchives = () => {
    if (pendingDownload) {
      const csv = buildStatisticsCsv(t, pendingDownload.columns as MUIDataTableColumn[], pendingDownload.rows, CSV_META);
      triggerCsvDownload(csv, generateFileName());
    }
    closeDialog();
  };

  const handleDownloadWithArchives = async () => {
    try {
      const userIds = Array.from(new Set(data.map((row) => row.userId)));
      const lessonIds = Array.from(new Set(data.map((row) => row.lessonId)));
      await downloadStatisticsWithArchives({ userIds, lessonIds });
    } catch (error) {
      handleApiError(error, { t, enqueueSnackbar });
    } finally {
      closeDialog();
    }
  };

  return (
    <StyledTableContainer>
      {gettingStatistics ? (
        <StyledLoadingContainer>
          <CircularProgress />
        </StyledLoadingContainer>
      ) : (
        <OverridenGlobalStyles>
          <StyledMUIDataTable title={t('statistics.usersResult')} data={tableData} columns={columns} options={{
            textLabels: {
              body: {
                noMatch: t('dataTable.noMatch'),
                toolTip: t('dataTable.toolTip'),
                columnHeaderTooltip: column => t('dataTable.columnHeaderTooltip', { column: column.label })
              },
              pagination: {
                next: t('dataTable.next'),
                previous: t('dataTable.previous'),
                rowsPerPage: t('dataTable.rowsPerPage'),
                displayRows: t('dataTable.displayRows'),
              },
              toolbar: {
                search: t('dataTable.search'),
                downloadCsv: t('dataTable.downloadCsv'),
                print: t('dataTable.print'),
                viewColumns: t('dataTable.viewColumns'),
                filterTable: t('dataTable.filterTable'),
              },
              filter: {
                all: t('dataTable.all'),
                title: t('dataTable.title'),
                reset: t('dataTable.reset'),
              },
              viewColumns: {
                title: t('dataTable.viewColumnsTitle'),
                titleAria: t('dataTable.viewColumnsTitleAria'),
              },
              selectedRows: {
                text: t('dataTable.selectedRowsText'),
                delete: t('dataTable.delete'),
                deleteAria: t('dataTable.deleteAria'),
              },
          },
          fixedHeader: false,
          download: true,
          downloadOptions: {
          filename: generateFileName(),
            separator: ';',
            filterOptions: {
              useDisplayedColumnsOnly: true,
              useDisplayedRowsOnly: true
            }
          },
          onDownload: (_bh, _bb, cols, rows) => {
            setPendingDownload({ columns: cols, rows: rows as Array<{ data: unknown[] }> });
            setDialogOpen(true);
            return false;
          },
          sortOrder,
          onColumnSortChange: (changedColumn, direction) => setSortOrder({ name: changedColumn, direction }),
          setTableProps: () => ({ style: { tableLayout: 'fixed' as const, minWidth: STATISTICS_TABLE_MIN_WIDTH } }),
          customToolbarSelect: (selectedRows, displayData, setSelectedRows) => (
            <CustomStatisticsToolbar
            selectedRows={selectedRows}
            displayData={displayData}
            adjustedData={tableData}
            columns={columns}
            t={t}
            onRequestDownload={(pending) => {
              setPendingDownload({ columns: pending.columns, rows: pending.rows });
              setDialogOpen(true);
            }}
            onGenerateCertificate={handleGenerateCertificate}
            generatingCertificate={generatingCsvCertificate}
            />
          ),
          onRowsDelete: () => false,
          }}/>
        </OverridenGlobalStyles>
      )}
      <ArchiveReportDialog
        open={dialogOpen}
        onConfirm={handleDownloadWithArchives}
        onDecline={handleDownloadWithoutArchives}
        onClose={closeDialog}
        loading={gettingStatisticsWithArchives}
      />
    </StyledTableContainer>
  );
};
